import { chunk } from "lodash";
import React, { useMemo, useState } from "react";
import styled, { css } from "styled-components";

import { Txt } from "../../hex-components";
import { useOnInitialMount } from "../../hooks/useOnInitialMount";

interface CustomerDataItem {
  author?: string;
  logoPath: string;
  name: string;
  quote?: string;
  slug: string;
  synopsis?: string;
  inHome: boolean;
}

interface CustomerDataPayload {
  feed_url: string;
  home_page_url: string;
  items: CustomerDataItem[];
  next_feed_url?: string;
  previous_feed_url?: string;
  version: string;
}

interface CustomerDataResponse {
  status: string;
  payload?: CustomerDataPayload;
  error?: Error;
}
const DESIRED_LOGO_SORT = [
  "Reddit",
  "Notion",
  "Brex",
  "Anthropic",

  "Hinge",
  "SeatGeek",
  "Babylist",
  "ClickUp",

  "Algolia",
  "Fivetran",
  "Loom",
  "AllTrails",

  "Hightouch",
  "Modern Treasury",
  "Ramp",
  "Sedgwick",
];

export const HexCustomerLogos: React.ComponentType = React.memo(
  function HexCustomerLogos() {
    const [customerData, setCustomerData] = useState<CustomerDataResponse>({
      status: "loading",
    });

    useOnInitialMount(() => {
      fetch("https://hex.tech/customersFeed-1.json")
        .then((response) => response.json())
        .then((response) =>
          setCustomerData({ status: "loaded", payload: response }),
        )
        .catch((error) => setCustomerData({ status: "error", error }));
    });

    const sortedChunkedLogos = useMemo(() => {
      const specifiedLogoSet = new Set(DESIRED_LOGO_SORT);
      if (!customerData.payload) {
        return [];
      }
      const logosByName = new Map(
        customerData.payload.items.map((item) => [item.name, item]),
      );

      const sortedLogos: CustomerDataItem[] = [];

      for (const logoName of DESIRED_LOGO_SORT) {
        const logo = logosByName.get(logoName);
        if (logo) {
          sortedLogos.push(logo);
        }
      }

      for (const logo of customerData.payload.items) {
        if (!specifiedLogoSet.has(logo.name)) {
          sortedLogos.push(logo);
        }
      }

      return chunk(sortedLogos.slice(0, 16), 4);
    }, [customerData]);

    return (
      <Wrapper $isVisible={customerData.status === "loaded"}>
        <LogoContainer>
          <TrustedBy>
            <Txt css="padding: 0 8px;" fontSize="small">
              Trusted by leading data companies
            </Txt>
            <TrustedByLine $position="left" />
            <TrustedByLine $position="right" />
          </TrustedBy>
          {sortedChunkedLogos.map((logos, i) => (
            <CustomerLogoRow key={i}>
              {logos.map((logo) => (
                <CustomerLogo key={logo.name}>
                  <img alt={logo.name} src={logo.logoPath} />
                </CustomerLogo>
              ))}
            </CustomerLogoRow>
          ))}
        </LogoContainer>
      </Wrapper>
    );
  },
);

const Wrapper = styled.div<{ $isVisible: boolean }>`
  color: ${({ theme }) => theme.marketingColors.ROSE_QUARTZ};
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: end;
  align-items: center;
  margin-right: 60px;

  opacity: 0;

  transition: opacity ${({ theme }) => theme.animation.durationMedium}
    ${({ theme }) => theme.animation.easing};

  ${({ $isVisible }) =>
    $isVisible &&
    css`
      opacity: 1;
    `}

  @media (max-width: ${({ theme }) => `${theme.mediaQuery.MEDIUM}px`}) {
    display: none;
  }
`;

const LogoContainer = styled.div`
  height: 600px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 30px;
  margin-right: 30px;
  margin-bottom: 25px;
`;

const CustomerLogoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 30px;
`;

const CustomerLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  > img {
    width: 80%;
  }
`;

const TrustedBy = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  text-transform: uppercase;
  opacity: 0.5;
  margin-bottom: 10px;

  &::before,
  &::after {
    flex: 1 1 auto;
    border-top: 1px solid ${({ theme }) => theme.marketingColors.ROSE_QUARTZ};
    content: "";
  }
`;

const TrustedByLine = styled.div<{ $position: "left" | "right" }>`
  position: absolute;
  height: 10px;
  border-left: 1px solid ${({ theme }) => theme.marketingColors.ROSE_QUARTZ};
  ${({ $position }) => ($position === "left" ? "left: 0;" : "right: 0;")}
`;
